import { Grid, GridColumn as Column, GridColumnMenuProps, GridColumnMenuCheckboxFilter, GridColumnMenuFilter, GridSelectionChangeEvent, GridCellProps, GridHeaderSelectionChangeEvent, GridPageChangeEvent, GridToolbar, GridColumn, GridFilterChangeEvent, GridColumnResizeEvent, GridColumnReorderEvent, GridDataStateChangeEvent, cellOperatorChange, cellInputChange } from '@progress/kendo-react-grid';
import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocalization } from '@progress/kendo-react-intl';
import { Popup } from '@progress/kendo-react-popup';
import { Dialog } from "@progress/kendo-react-dialogs";

import { AccessToken } from '../config/AccessToken';
import { useNavigate } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import { HeaderThElement, PagerTargetEvent } from '@progress/kendo-react-data-tools';
import { fileCsvIcon, fileExcelIcon, filePdfIcon, filterIcon, saveIcon, uploadIcon } from '@progress/kendo-svg-icons';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { GridPDFExport } from '@progress/kendo-react-pdf';

import { process as kendoProcess } from "@progress/kendo-data-query";
import { CSVLink } from 'react-csv';
import { json2csv } from 'json-2-csv';
import { SvgIcon } from '@progress/kendo-react-common';
import { Window } from "@progress/kendo-react-dialogs";
import { loadingPanel } from '../components/LoadingPanel';
import { ConfiguratorToggleColumnsGrid } from '../components/ConfiguratorToggleColumnsGrid';
import { DatePicker, TimePicker } from '@progress/kendo-react-dateinputs';
import { DropDownList } from '@progress/kendo-react-dropdowns';


interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };


/**
 * Component to manage and display alarms in a grid format.
 * 
 * This component fetches and displays a list of alarms, allowing the user to
 * filter, sort, and paginate through the alarms. It also provides functionality
 * for exporting the data to Excel, CSV, and PDF formats, as well as saving and
 * loading the grid state configuration.
 * 
 * Methods:
 *  - getAlarmList: Fetches the list of alarms from the server.
 *  - pageChange: Handles page change events.
 *  - onSelectedItemsChange: Handles changes in selected items.
 *  - onHeaderSelection: Handles header selection changes.
 *  - ColumnMenuCheckboxFilter: Renders column menu with checkbox filters.
 *  - getInfoTagAlarm, toggleAlarm: Renders custom cell content for grid.
 *  - InfoPopupRender: Renders popup content for alarm info.
 *  - disableAlarm: Disables selected alarms.
 *  - saveStateToLocalStorage, loadStateFromLocalStorage: Save/load grid state.
 *  - setWidth: Calculates column widths.
 *  - excelExport, pdfExport: Handles data export to Excel and PDF.
 *  - onSearchChange: Handles changes in the search input.
 *  - onFilterChange: Handles filter change events.
 *  - onColumnReorder, onColumnResize: Handles column reorder/resize events.
 *  - onDataStateChange: Handles data state changes for paging and filtering.
**/

export const Alarms = (props?: any) => {

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  const [alarms, setAlarms] = useState<any>([]);
  const [totalAlarms, setTotalAlarms] = useState<any>([]);
  const [alarmInfo, setAlarmInfo] = useState<any>([]);
  const [error, setError] = useState('');
  const [rowSelected, setRowSelected] = useState<any>([]);
  
  const [processedData, setProcessedData] = useState<any>([]);

  const [selectedState, setSelectedState] = useState<{ [id: string]: boolean | number[] }>({});

  const [pageIndex, setPageIndex] = useState<any>(initialDataState);
  const [totalPages, setTotalPages] = useState<any>();
  const [totalItems, setTotalItems] = useState<number>();
  const [hasPreviousPage, setHasPreviousPage] = useState<any>();
  const [hasNextPage, setHasNextPage] = useState<any>();

  const [disableAllAlarms, setDisableAllAlarms] = useState(false);

  const ADJUST_PADDING: number = 24;
  const COLUMN_MIN: number = 4;
  const minGridWidth = useRef<number>(0);
  const grid = useRef<any>(null);
  const [applyMinWidth, setApplyMinWidth] = useState(false);
  const [gridCurrent, setGridCurrent] = useState(0);

  const [dateValue, setDateValue] = useState<Date | null>(new Date());

  const [columns, setColumns] = useState<any>([]);  

  const [filterName, setFilterName] = useState<any>([]);

  const [showPopupInfo, setShowPopupInfo] = useState(false);
  
  const [loading, setLoading] = useState(false);

  const DATA_ITEM_KEY: string = 'id';

  const localizationService = useLocalization();

  const [instance, accessTokenRequest] = AccessToken();

  const navigate = useNavigate()

  const userEmail = localStorage.getItem('email');


  const ColumnMenu = (props: GridColumnMenuProps) => {
    const customFilterOperators:any = {
      date: [
        { text: 'grid.filterNotEqOperator', operator: 'neq' },
        { text: 'grid.filterAfterOrEqualOperator', operator: 'gte' },
        { text: 'grid.filterAfterOperator', operator: 'gt' },
        { text: 'grid.filterBeforeOperator', operator: 'lt' },
        { text: 'grid.filterBeforeOrEqualOperator', operator: 'lte' },
        //{ text: 'grid.filterIsNullOperator', operator: 'isnull' },
        //{ text: 'grid.filterIsNotNullOperator', operator: 'isnotnull' }
      ]
    };

    const handleFilterChange = (filterValue: any) => {    
      setDataState((prevState: any) => ({
        ...prevState,
        filter: filterValue
      }));
      let filterName:any=[]
      filterValue?.filters?.forEach((filter:any) => {
        filter?.filters?.forEach((filterInfo:any) => {
          filterName.push(filterInfo.field)
        })
      })
      setFilterName(filterName)
      localStorage.setItem("filterNameAlarm", JSON.stringify(filterName));
      
    };

    return (
      <div>
        <GridColumnMenuFilter 
          {...props} 
          expanded={true} 
          filterOperators={customFilterOperators}
          onFilterChange={handleFilterChange}          
        />
      </div>
    );
  }

  const ColumnMenuCheckboxFilter = (props: GridColumnMenuProps) => {
    return (
      <div>
        <GridColumnMenuCheckboxFilter {...props} data={alarms} expanded={true} searchBoxFilterOperator={'contains'}/>
      </div>
    );
  }

  const removeFilterByField = (filterObject: any, fieldName: any): any => {
    if (!filterObject || !filterObject.filters || !Array.isArray(filterObject.filters)) {
      return { logic: 'and', filters: [] };
    }
    
    const updatedFilters = filterObject.filters
    .map((item: any) => {
      // Se è il filtro da rimuovere
      if (item.field === fieldName) {
        return null;
            }
            
            // Se è un gruppo di filtri annidato
            if (item.filters && Array.isArray(item.filters)) {
              const updatedGroup = removeFilterByField(item, fieldName);
                // Mantieni solo se il gruppo ha ancora filtri
                if (updatedGroup.filters.length > 0) {
                    return updatedGroup;
                  } else {
                    return null;
                  }
                }
            return item;
        })
        .filter((item: any) => item !== null);

        // Sempre restituire un oggetto con 'logic' e 'filters'
        return {
          logic: filterObject.logic || 'and',
          filters: updatedFilters
    };
  };

  const GridColumnMenuFilterCell = (props:any) => {

    const gridColumnMenuFilterCell = useRef<any>(null);

    useEffect(() => {
      
      const handleDocumentClick = (e: MouseEvent) => {
        const button = (e.target as HTMLElement).closest('button[type="reset"]');
        if (button) {
          handleResetClick();
        }
      };
  
      document.body.addEventListener('click', handleDocumentClick);
      return () => document.body.removeEventListener('click', handleDocumentClick);
    }, []);

    const handleResetClick = () => {
      const field = props.field;
  
      // Rimuove il filtro solo per la colonna corrente
      const currentFilter = dataState.filter;
      if (!currentFilter || !Array.isArray(currentFilter.filters)) return;
  
      // Applichiamo la rimozione ricorsiva
      const updatedFilterObject = removeFilterByField(currentFilter, field);

      const newState = {
        ...dataState,
        filter: {
          logic: 'and',
          filters: updatedFilterObject.filters
        }
      };
  
      setDataState(newState);
      setFilterName((prev: any) => prev.filter((item: any) => item !== field));
      localStorage.setItem(`gridAlarmState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
      localStorage.removeItem('filterOpenedColumn');
    };

    useEffect(() => {
      // Questo effetto scatta all'apertura del filtro
      localStorage.setItem('filterOpenedColumn', 'openedColumn');
    }, []);

    const [selectedOperator, setSelectedOperator] = useState(props.operator || 'eq');    

    const addFilter = (obj: any, filtro: any, logic = "or") => {
      // Trova il prossimo indice disponibile
      let keys = Object.keys(obj).map(k => parseInt(k));
      let nextIndex = keys.length > 0 ? Math.max(...keys) + 1 : 0;
  
      // Aggiungi il nuovo filtro
      obj[nextIndex.toString()] = {
          "logic": logic,
          "filters": [filtro]
      };
  
      return obj;
    }

    const containsDateTimeField = (filters:any):boolean => {
      if (!Array.isArray(filters)) return false;
  
      return filters.some((filter:any) => {
          if (filter.field === 'dateTime') {
              return true;
          } 
          // Se ha sotto-filtri, ricorsivamente cerca anche lì
          if (Array.isArray(filter.filters)) {
              return containsDateTimeField(filter.filters);
          }
          return false;
      });
    };

    const replaceOrNestDateTimeFilterAtTop = (filters: any[], newFilter: any): any[] => {
      let found = false;
  
      // Ricorsivamente sostituisce
      const recursiveReplace = (filtersArray: any[]): any[] => {
          return filtersArray.map((f: any) => {
              if (f.filters && Array.isArray(f.filters)) {
                  return { ...f, filters: recursiveReplace(f.filters) };
              } else if (f.field === 'dateTime') {
                  found = true;
                  return newFilter;
              }
              return f;
          });
      };
  
      let updatedFilters = recursiveReplace(filters);
  
      if (!found) {
          // Se non trovato, crea un nuovo gruppo 'or' con dateTime
          const dateTimeGroup = {
              logic: 'or',
              filters: [newFilter]
          };
  
          // Inserisci il gruppo dateTime sempre in posizione 0
          //updatedFilters = [dateTimeGroup, ...updatedFilters];
          addFilter(updatedFilters, dateTimeGroup, 'or');
      } 
      return updatedFilters;
    };
  

    const inputChange = (value:any, e:any) => {
      cellInputChange(value, e, props);

      setDateValue(value)

      if (value == null) {
        return;
      } 

      value.setUTCMilliseconds(0);
      value = value.toISOString()

      const filterObj = {   
        ...dataState?.filter
      };

      const newFilter = {
        "field": props.field,
        "operator": selectedOperator,
        "value": value
      };

      if(filterObj?.filters == undefined){
        filterObj.logic = 'and';
        filterObj.filters = [{                              
          logic: "or",
          filters:[ newFilter ]
        }]
      }else{
        if(containsDateTimeField(filterObj.filters)){
          filterObj.filters = replaceOrNestDateTimeFilterAtTop(filterObj.filters, newFilter);
        }else{
          addFilter(filterObj.filters, newFilter, 'or');
        }
      }
        
      setDataState((prevState: any) => ({
        ...prevState,
        filter: filterObj
      }));

      // Set filter name for tracking
      const filterName = props.field;
      
      setFilterName((prevFilterName:any) => {
        return [...prevFilterName, filterName];
      });
      
      // Salva subito lo stato aggiornato
      const newState = {
        dataState: {
          ...dataState,
          filter: filterObj
        },
        columns
      };
      localStorage.setItem(`gridAlarmState: ${userEmail}`, JSON.stringify(newState));

      getAlarmList();

    };    
  
    const operatorChange = (event:any) => {
      const newOperator = event.value.operator;
      setSelectedOperator(newOperator); // <-- Track selected operator
      cellOperatorChange(event, props.value, props.onChange);
    };
  
    const renderOperatorEditor = () => {
      const { operators } = props;

      const filteredOperators = operators.filter(
        (op: any) => op.operator !== 'isnull' && op.operator !== 'isnotnull'
      );
  
      const value = filteredOperators.find((item: any) => item.operator === selectedOperator) || null;
  
      return (
        <DropDownList
          value={value}
          onChange={operatorChange}
          data={filteredOperators}
          textField="text"
        />
      );
    };
  
    return (
      <div onClick={(e) => e.stopPropagation()}>
        {renderOperatorEditor()}

        <DatePicker    
          popupSettings={{ appendTo: document.body }}
          ref={gridColumnMenuFilterCell}      
          value={dateValue}
          format="dd/MM/yyyy"
          formatPlaceholder={'formatPattern'}
          onChange={(e:any) => inputChange(e.value, e.syntheticEvent)}
          onClick={(e) => e.stopPropagation()}
        />
        
        <div style={{marginBottom: '10px'}}></div>

        <TimePicker
          value={dateValue}
          format="HH:mm:ss" // Include seconds in the format
          formatPlaceholder={'formatPattern'}
          onChange={(e) => inputChange(e.value, e.syntheticEvent)}
        />
      </div>
    );
  };

  const CustomGridFilterDateTime = (props:any) => {
    const {
      firstFilterProps
    } = props;

     
    return (
      <div>
        <GridColumnMenuFilterCell           
          {...firstFilterProps} 
        />
      </div>
    );
  };

  const InfoTagAlarmComponent = (props: any) => {
    const tagInfo = props.dataItem.info
    return (
      <td
        className="k-icon k-font-icon k-i-information"
        style={{ zoom: '1.5', cursor: 'pointer' }}
        onClick={() => {
          setAlarmInfo(tagInfo);
          setShowPopupInfo(true);
        }
        }
      >
      </td>
    )
  };

  const currColumns: any = [
    { field: 'deviceMac', title: "Mac", columnMenu: ColumnMenuCheckboxFilter, minWidth: 100, width: props.preview ? '' : 250, filterable: false, show: true },
    { field: 'name', title: localizationService.toLanguageString('alarms.name', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, minWidth: 160, width: props.preview ? '' : 200, filterable: false, show: true },
    { field: 'zoneName', title: localizationService.toLanguageString('alarms.zone', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, minWidth: 160, width: props.preview ? '' : 200, filterable: false, show: true },
    { field: 'dateTime', title: localizationService.toLanguageString('alarms.date_time', 'NOT_FOUND'), minWidth: 160, width: props.preview ? '' : 300, editable: false, filter: 'date', format: '{0:dd/MM/yyyy HH:mm:ss}', show: true },
    { field: 'eventType.name', title: localizationService.toLanguageString('alarms.type', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, minWidth: props.preview ? 200 : 160, width: props.preview ? '' : 300, filterable: false, editable: false, show: true },
    { field: 'eventType.description', title: localizationService.toLanguageString('alarms.description', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, minWidth: props.preview ? 450 : 400, width: 300, filterable: false, editable: false, show: true },
    { field: 'info', title: localizationService.toLanguageString('alarms.info', 'NOT_FOUND'), minWidth: 80, width: props.preview ? 0 : 80, cell: !props.preview ? InfoTagAlarmComponent : undefined, filterable: false, editable: false, show: props.preview ? false : true},
    { field: 'groupName', title: localizationService.toLanguageString('alarms.group', 'NOT_FOUND'), minWidth: 160, width: props.preview ? '0px' : 200, columnMenu: props.preview ? '' : ColumnMenuCheckboxFilter, filterable: false, editable: false, show: true }
  ];

  const showMapFromSelectedAlarm = (event: any) => {
    let alarmInfoSelected = event.dataItem;
    localStorage.setItem("alarmInfoSelected", JSON.stringify(alarmInfoSelected));    
    navigate("/home/map")
  }

  const deviceMacFromSynoptic = localStorage.getItem('deviceMacFromSynoptic');
  useEffect(() => {
    if(deviceMacFromSynoptic != null){
      getAlarmListByDeviceMac(deviceMacFromSynoptic)      
    }else{
      getAlarmList()
    }
  }, [])

  const getAlarmList = async (skip?: any, take?: any) => {

    setLoading(true);

    let languageId = localStorage.getItem('languageId');

    const commonOperations = (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      const params = {
        pageIndex: skip ?? pageIndex?.skip,
        pageSize: take ?? pageIndex?.take
      };
      axios.get(`${SERVER_URL}/alarm/list`, { headers, params })
        .then((res:any) => {
          if(res.length == 0) return;
          
          const data = res?.data.data

          const alarmsInfo:any = []

          // format date in data.data
          data?.items?.forEach((alarm: any) => {
            if (alarm.dateTime == null) {
              alarm.dateTime = localizationService.toLanguageString('alarms.date_not_found', 'NOT_FOUND')
              return
            }
            alarm.dateTime = formatDate(alarm?.dateTime)

            alarmsInfo.push(alarm)
          })

          setAlarms(alarmsInfo)
          setTotalPages(data.totalPages)
          setTotalAlarms(data.totalItems)
          setTotalItems(data.totalItems)
          setHasPreviousPage(data.hasPreviousPage)
          setHasNextPage(data.hasNextPage)

          //setColumns(currColumns);
          //setCurrentColumn(currColumns);

          setLoading(false);

        }).catch((data) => {
          setError(data?.message);
          setLoading(false);
        });
    }

    if (sessionStorage.getItem('accessToken') != null) {
      let accessToken = sessionStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
    }
  }

  const formatDate = (date: any) => {
    if(date != undefined){
      // Split the date and time
      const [day, month, year] = date?.split(" ")[0]?.split("/"); 

      let dateParts = date?.split(" ");
      const timeValues:any = dateParts[1]?.split(":");
      
      let hour = parseInt(timeValues[0], 10);
      let minute = parseInt(timeValues[1], 10);
      let second = parseInt(timeValues[2], 10);
      
      // Convert the string to a Date object
      let dateConvert = new Date(Date.UTC(year, month-1, day, hour, minute, second))
      
      // Convert the date to the user's local timezone
      let localDate = dateConvert?.toLocaleString(navigator.language).replace(/,/g, '')
      
      const timeConvert = localDate?.split(" ")[1];
      const timeValuesConvert:any = timeConvert?.split(":");
      const formattedHours = String(timeValuesConvert[0]).padStart(2, '0'); // Add leading zero if needed
      const formattedMinutes = String(timeValuesConvert[1]).padStart(2, '0');
      const formattedSeconds = String(timeValuesConvert[2]).padStart(2, '0');
      const formattedTime = `${formattedHours}:${formattedMinutes}:${formattedSeconds}`
      
      if(navigator.language == 'en' || navigator.language == 'en-US' || navigator.language == 'en-GB'){
        const [monthConvert, dayConvert, yearConvert] = localDate?.split(" ")[0].split("/"); 
        const formattedMonth = String(monthConvert).padStart(2, '0'); // Add leading zero if needed
        const formattedDay = String(dayConvert).padStart(2, '0');
        const validDate = `${yearConvert}-${formattedMonth}-${formattedDay}T${formattedTime}`;
        return new Date(validDate);
      }
      else{
        const [dayConvert, monthConvert, yearConvert] = localDate?.split(" ")[0].split("/"); 
        const formattedMonth = String(monthConvert).padStart(2, '0'); // Add leading zero if needed
        const formattedDay = String(dayConvert).padStart(2, '0');
        const validDate = `${yearConvert}-${formattedMonth}-${formattedDay}T${timeConvert}`;
        return new Date(validDate);
      }
    }else{
      return 0
    }
  }

  const getAlarmListByDeviceMac = async (deviceMac:any, skip?: any, take?: any) => {

    setLoading(true);

    let languageId = localStorage.getItem('languageId');

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      
      await axios.get(`${SERVER_URL}/alarm/ListAlarmsByMac?mac=${deviceMac}`, { headers })
        .then((res:any) => {
          
          const data = res?.data.data
          if(data.items.length == 0) return;

          // format date in data.data
          data?.items?.forEach((alarm: any) => {
            if (alarm.dateTime == null) {
              alarm.dateTime = localizationService.toLanguageString('alarms.date_not_found', 'NOT_FOUND')
              return
            }
            alarm.dateTime = alarm?.dateTime?.split('T')[0]

            //format alarm.dateTime with gg/mm/yyyy
            var dateParts = alarm.dateTime.split("/");

            var yearFormatted:any = dateParts[2].split(' ')[0]

            const day = dateParts[0] //date?.getDay();
            const month = dateParts[1] //(date.getMonth() + 1).toString().padStart(2, "0");
            const year = yearFormatted;
            alarm.dateTime = new Date(`${year}, ${month},${day}`)
          })
          
          setAlarms(data?.items)

          setTotalPages(data.totalPages)
          setTotalItems(data.totalItems)
          setHasPreviousPage(data.hasPreviousPage)
          setHasNextPage(data.hasNextPage)

                    
          setLoading(false);          

        }).catch((data) => {
          setError(data?.message);
          setLoading(false);
        });
    }

    if (sessionStorage.getItem('accessToken') != null) {
      let accessToken = sessionStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
    }
  }
    
  const exportRecords = async (format:any) => {

    setLoading(true);

    let languageId = localStorage.getItem('languageId');

    const commonOperations = (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      
      axios.get(`${SERVER_URL}/alarm/download?format=${format}`, { headers, responseType: 'blob' })
        .then((response) => {

          const type:any = format == 'csv' ? 'text/csv;charset=utf-8;' : format == 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf'
          
          const ext:any = format == 'csv' ? '.csv' : format == 'excel' ? '.xlsx' : '.pdf'

          const blob = new Blob([response.data], { type: type });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.setAttribute("href", url);
          link.setAttribute("download", `records${ext}`);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          setLoading(false);

        }).catch((error) => {
          if (error.response) {
            setError(error.response.data.message);
          } else {
            setError(error.message);
          }
          setLoading(false);
        });
    }

    if (sessionStorage.getItem('accessToken') != null) {
      let accessToken = sessionStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
    }
  }

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent: any = event?.targetEvent as PagerTargetEvent;
    const take: any = targetEvent?.value === "All" ? totalItems : event?.page?.take;
    const skip: any = event?.page?.skip;
    //setDataState((prevState: any) => ({
    //  ...prevState,
    //  skip: event?.page?.skip,
    //  take: event.page.take
    //}));

    setPageIndex({
      ...event?.page,
      take,
    });

    if(deviceMacFromSynoptic != null){
      getAlarmListByDeviceMac(deviceMacFromSynoptic, (skip / take) + 1, take)
    }else{
      getAlarmList((skip / take) + 1, take)
    }
  };

  // used for checkbox selection
  const onSelectedItemsChange = useCallback((event: GridSelectionChangeEvent) => {
    setRowSelected(event?.dataItems?.map((item: any) => item.id) || [])

    if (event?.syntheticEvent?.currentTarget?.checked) {
      props.handlePopupAlarm()
    }
  },
    [selectedState]
  );

  const onHeaderSelection = useCallback((event: GridHeaderSelectionChangeEvent) => {

    const newSelectedState: any = [];

    let data: any = event.dataItems ?? event

    data.forEach((item: any) => {
      newSelectedState.push(item.id)
    });
    setSelectedState(newSelectedState);
    setRowSelected(newSelectedState)
    props.handlePopupAlarm()
  },
    []
  );
  
  const toggleAlarm = (propsGrid: GridCellProps) => {

    return (
      <td className="k-command-cell" style={{textAlign: 'center'}}>       
        <button
          id='disabled-alarm'
          className="k-icon k-font-icon k-i-bell"
          //style={{ display: 'block', width: '100%', color: '#198792', border: 'none', background: 'none', outline: 'none' }}
          style={{ color: '#198792', margin: '8px', border: 'none', background: 'none', outline: 'none' }}
          onClick={() => 
            {
              setRowSelected(propsGrid.dataItem.id)
              setDisableAllAlarms(false)
              props.handlePopupAlarm()
            }
          }
        >
          <a  id='disabled-alarm' />
        </button>
      </td>    
    )
  };

  const disableAlarm = async () => {

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}` };
      const params = {
        accountId: -1,
        alarmIds: Array.isArray(rowSelected) ? rowSelected : [rowSelected],
      };
      try {
        await axios.post(`${SERVER_URL}/alarm/ackalarm`, params, { headers })
        props.handlePopupAlarm()
        getAlarmList()
      } catch (error: any) {
        setError(error);
      }
    }

    if (sessionStorage.getItem('accessToken') != null) {
      let accessToken = sessionStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken)
      })
    }
  }

  const [dataState, setDataState] = useState<any>({
    skip: 0,
    take: pageIndex?.take ?? 10,
    data: alarms,
    total: alarms?.length,
    filter: { logic: 'and', filters: [] },
    sort: [{ field: 'mac', dir: 'desc' }]
  });

  const saveStateToLocalStorage = () => {
    const state: any = { dataState, columns };
    localStorage.setItem(`gridAlarmState: ${userEmail}`, JSON.stringify(state));
  };

  const loadStateFromLocalStorage = () => {
    const storedFilter = localStorage.getItem(`gridAlarmState: ${userEmail}`);
    if (storedFilter) {
      const { dataState: savedDataState, columns: savedColumns } = JSON.parse(storedFilter);

      if (savedDataState?.filter != null) {
        // Funzione ricorsiva per gestire qualsiasi struttura di filtri
        const updateFilterValues = (filter: any): any => {
          if (Array.isArray(filter.filters)) {
            return {
              ...filter,
              filters: filter.filters.map(updateFilterValues),
            };
          } else {
            const field = filter?.field;
            let value = filter?.value;
  
            if (['dateTime'].includes(field)) {
              value = new Date(value);
            }
  
            return {
              ...filter,
              value,
            };
          }
        };
  
        const updatedFilter = updateFilterValues(savedDataState.filter);
  
        const updatedSavedDataState = {
          ...savedDataState,
          filter: updatedFilter,
        };
  
        setDataState(updatedSavedDataState);
      } else {
        setDataState(savedDataState);
      }

      //let currentColumn:any = [...savedColumns];
      let currentColumn:any = savedColumns?.map((column: any, id: any) => {
        if(column.id == id){
          return { ...column, show: !column.show }; // Create a *new* object
        }
        return column; // Return the other columns unchanged
      });

      //setColumns(currentColumn);

      return storedFilter
        ? storedFilter
        : currentColumn;
    }
  };

  useEffect(() => {
    setColumns(currColumns)
    loadStateFromLocalStorage();
  }, []);

  const anchor = useRef<any>(null);
  const [showPopupSaveConfig, setShowPopupSaveConfig] = useState(false);

  const onClickSaveConfig = () => {
    setShowPopupSaveConfig(!showPopupSaveConfig);

    setTimeout(() => {
      setShowPopupSaveConfig(showPopupSaveConfig);
    }, 3000)

  };

  const setWidth = (minWidth: number | undefined) => {
    if (minWidth === undefined) {
      minWidth = 0;
    }
    let width = applyMinWidth
      ? minWidth
      : minWidth + (gridCurrent - minGridWidth.current) / (columns?.length ?? columns?.columns?.length);
    if (width >= COLUMN_MIN) {
      width -= ADJUST_PADDING;
    }
    return width;
  };

  const CellRender = (props: any) => {
    return (
      <td
          {...props.tdProps}
          colSpan={1}
      >
          {props.children}
      </td>
    );
  }

  const onFilterChange = (event: GridFilterChangeEvent) => {
    const newFilter: any = event.filter
      ? { ...event.filter }
      : { logic: 'and', filters: [] };

    setDataState((prevState: any) => ({
      ...prevState,
      filter: newFilter
    }));

    let filterName:any=[]
    newFilter?.filters?.forEach((filter:any) => {
      filter?.filters?.forEach((filterInfo:any) => {
        filterName.push(filterInfo.field)
      })
    })
    setFilterName(filterName)
    localStorage.setItem("filterNameAlarm", JSON.stringify(filterName));
  };

  const anchorSave = useRef<any>(null);

  const onChange = (event: any) => {
    const field = event.target.name;
    const newColumns = columns.map((column: any) => {
      if (column.field === field) {
        return { ...column, show: !column.show };
      }
      return column;
    });
    setColumns(newColumns);
    
    const state = { dataState, columns: newColumns };
    localStorage.setItem(`gridAlarmState: ${userEmail}`, JSON.stringify(state));
  };

  const onColumnReorder = (event: GridColumnReorderEvent) => {
    setColumns(event.columns);
    saveStateToLocalStorage();
  };

  const onColumnResize = (event: GridColumnResizeEvent) => {
    setColumns(event.columns);
    saveStateToLocalStorage();
  };

  useEffect(() => {
    const result = kendoProcess(alarms ?? [], {
        filter: dataState?.filter,
        sort: dataState?.sort,
        skip: dataState?.skip,
        take: pageIndex?.take ?? dataState?.take,
    });

    setProcessedData(result.data);
    if(filterName.length != 0){
      setTotalItems(result.total);
    }else{
      setTotalItems(totalAlarms);
    }

  }, [alarms, filterName]);
  
  const onDataStateChange = (event: any) => {
    
    const newFilter: any = event.dataState.filter
      ? { ...event.dataState.filter }
      : { logic: 'and', filters: [] };

    setDataState((prevState: any) => ({
      ...prevState,
      filter: newFilter
    }));

    let filterName:any=[]
    newFilter?.filters?.forEach((filter:any) => {
      filter?.filters?.forEach((filterInfo:any) => {
        filterName.push(filterInfo.field)
      })
    })
    setFilterName(filterName)
    localStorage.setItem("filterNameAlarm", JSON.stringify(filterName));

    const { dataState } = event;
    if (dataState) {
      const { filter, sort, skip, take } = dataState;
      const updatedFilter = filter || dataState.filter;
      const updatedSkip = skip !== undefined ? skip : dataState.skip;
      const updatedTake = take !== undefined ? take : dataState.take;
      setDataState((prevState: any) => ({
        ...prevState,
        filter: updatedFilter,
        sort: sort,
        skip: updatedSkip,
        take: updatedTake
      }));      
    }
    const state = { dataState, columns };
    localStorage.setItem(`gridAlarmState: ${userEmail}`, JSON.stringify(state));
  };

  const formatDateFromIsoToString = (isoString:any) => {
    const date = new Date(isoString);
  
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
  
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  }
  
  const DateTimeCellWithBackGroundComponent = (props: any) => {
    const dateTimeValue:any = formatDateFromIsoToString(props.dataItem.dateTime);  
    if(dateTimeValue != null && !dateTimeValue.includes('1970')){   
      return(
        <td>
          <span style={{display: 'block', width: '200px'}}>{dateTimeValue}</span>
        </td>
      )
    } else {
      return(
        <td><span/></td>
      )
    }
  };

  const IconHeaderCell = (props: any) => {  
    localStorage.setItem('filterNameAlarm', JSON.stringify(filterName));
    
    localStorage.removeItem('filterOpenedColumn');
    const savedFilterName:any = localStorage.getItem('filterNameAlarm');
    
    return savedFilterName?.includes(props.field) ?       
      <HeaderThElement columnId={props.thProps?.columnId || ''} {...props.thProps} >
        <span style={{display:'inline-flex'}}>{props.children} <SvgIcon icon={filterIcon} size="medium"/></span>
      </HeaderThElement>    
    :
      <HeaderThElement columnId={props.thProps?.columnId || ''} {...props.thProps} >
        {props.children}
      </HeaderThElement>
  };


  return (
    <div 
      style={{ 
        display: 'grid',
        width: '98%',
        margin: '10px',        
        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 15px 5px'        
      }}
    >
      {error && <p className="text-danger" style={{ zIndex: 999, position: 'relative', width: '65%' }}>{error}</p>}
      {alarms == null && loadingPanel}
      {loading && loadingPanel}
                   
        {/*<ExcelExport data={alarms} ref={_excelExport}>*/}
        <ExcelExport data={alarms} >
          <Grid
            data={processedData}
            dataItemKey={'id'}
            cells={{ data: CellRender}}
            total={totalItems}
            //skip={pageIndex.skip}
            //take={pageIndex.take}
            sortable={true}
            reorderable={false}
            defaultTake={10}
            defaultSkip={0}
            pageable={{
              type: 'input',
              buttonCount: 5,
              pageSizes: [5, 10, 50],
              info: true
            }}
            sort={dataState.sort}
            filter={dataState.filter}
            onSelectionChange={onSelectedItemsChange}
            onHeaderSelectionChange={onHeaderSelection}
            onPageChange={pageChange}
            //onFilterChange={onFilterChange}
            onColumnReorder={onColumnReorder}
            onColumnResize={onColumnResize}
            onDataStateChange={onDataStateChange}
            scrollable={'none'}
          >
            <GridToolbar className='k-grid-toolbar-alarms'>
              <div>
                <h3>{localizationService.toLanguageString('alarms.title', 'NOT_FOUND')}</h3>
              </div>
              {!props.preview ?
                <div style={{alignItems: 'baseline'}}>
                  <button
                    className='alarm-page disable-alarm-button'
                    onClick={() => {
                      onHeaderSelection(alarms);
                      setDisableAllAlarms(true)
                    }}
                  >
                    {localizationService.toLanguageString('alarms.disable_all_alarms_button', 'NOT_FOUND')}
                  </button>
                  { deviceMacFromSynoptic ?
                    <div 
                      style={{
                        color: '#1A8894',
                        marginLeft: '20px',
                      }}
                    >
                      <div style={{background: '#CED4DA', display: 'flex', height: '30px', alignItems: 'anchor-center', borderRadius: '5px'}}>
                        <p style={{fontSize: '14px', fontWeight:'bolder', marginBottom: '0px', marginLeft: '10px'}}>Mac:</p>
                        <p style={{
                            marginLeft: '10px',
                            fontSize: '14px',
                            alignContent: 'center',
                            marginBottom: '0px',
                            marginRight: '10px',
                          }}
                        >
                          {deviceMacFromSynoptic}
                        </p>
                        <Button 
                          className='alarmsFilterByMac'
                          onClick={()=>{
                            localStorage.removeItem('deviceMacFromSynoptic');
                            getAlarmList();
                          }}
                        >X</Button>
                      </div>
                    </div>
                    : null
                  }
                </div>
                : null}
                <Popup
                  anchor={anchor.current && anchor.current.element}
                  show={showPopupSaveConfig}
                  popupClass={"popup-content-save-synoptic-config"}
                >
                  {localizationService.toLanguageString('synoptic_devices.saved_config', 'NOT_FOUND')}
                </Popup>
              <div style={{ right: '15px', float: 'right', position: 'absolute', display: 'flex', gap: '8px' }}>
                <ConfiguratorToggleColumnsGrid columns={columns} onChange={onChange} />
                
                <div>
                  <Button
                    themeColor={"primary"}
                    size="small"
                    title={localizationService.toLanguageString('export_to_csv', 'NOT_FOUND')}
                    svgIcon={fileCsvIcon}
                    onClick={()=>exportRecords('csv')}
                  />
                </div>
                <div>
                  <Button
                    onClick={()=>exportRecords('pdf')}
                    themeColor={"primary"}
                    size="small"
                    title={localizationService.toLanguageString('export_to_pdf', 'NOT_FOUND')}
                    svgIcon={filePdfIcon}
                  />
                </div>
                <div>
                  <Button
                    title={localizationService.toLanguageString('export_to_excel', 'NOT_FOUND')}
                    themeColor={"primary"}
                    size="small"
                    svgIcon={fileExcelIcon}
                    onClick={()=>exportRecords('excel')}
                  />
                </div>
                {!props.preview ?
                  <div>
                    <Button
                      ref={anchor}
                      title={localizationService.toLanguageString('save_config', 'NOT_FOUND')}
                      themeColor={'primary'}
                      size="small"
                      svgIcon={saveIcon}
                      onClick={() => {
                        saveStateToLocalStorage();
                        onClickSaveConfig()
                      }}
                    >
                    </Button>
                  </div>
                  : null}
                {!props.preview ?
                  <div>
                    <Button
                      themeColor={'primary'}
                      size="small"
                      svgIcon={uploadIcon}
                      onClick={loadStateFromLocalStorage}
                      title={localizationService.toLanguageString('load_config', 'NOT_FOUND')}
                    >
                    </Button>
                  </div>
                  : null}
              </div>
            </GridToolbar>
            {!props.preview ?
              <Column
                cells={{data:(event:any) =>
                  <>
                  <td 
                    className="k-command-cell"  
                  >
                    <button
                      id='map'
                      className="k-icon k-font-icon k-i-globe"
                      style={{ 
                        color: '#198792', 
                        margin: '8px', 
                        border: 'none', 
                        background: 'none', 
                        outline: 'none', 
                        pointerEvents: event?.dataItem?.mapId == null ? 'none' : 'auto',
                        opacity: event?.dataItem?.mapId == null ? '0.5' : '1',
                      }}
                      title={localizationService.toLanguageString('beacons.map', 'NOT_FOUND')}
                      disabled={event?.dataItem?.mapId == null ? true : false}
                      onClick={() => showMapFromSelectedAlarm(event)}
                    >
                      <a
                        id='map'
                        title={localizationService.toLanguageString('alarms.map', 'NOT_FOUND')}
                      />
                    </button>
                  </td>
                  </>
                }}
                width={50}
                editable={false}
                filterable={false}
                sortable={false}
              />
              : null}
            {!props.preview ?
              <Column
                title={localizationService.toLanguageString('alarms.deactive', 'NOT_FOUND')}
                width={props.preview ? '0px' : 60}
                filterable={false}
                editable={false}
                sortable={false}
                cells={{data:toggleAlarm}}
              />
              : null}
            {
              columns?.length > 0 ?
                columns?.map((column: any, index: any) =>
                  column?.show && (
                    column.field == 'dateTime' ?
                      <Column              
                        field='dateTime'
                        title={localizationService.toLanguageString('alarms.date_time', 'NOT_FOUND')}
                        editable={false}
                        format={column.format}
                        columnMenu={(props) => (
                          <GridColumnMenuFilter
                            {...props}                        
                            alwaysExpand
                            filterUI={CustomGridFilterDateTime}
                            onExpandChange={() => {}}
                            onCloseMenu={() => {
                              const filterName = props.column.field;      
                              setFilterName((prevFilterName:any) => {
                                return prevFilterName.filter((item:any) => item !== filterName);
                              });
                              localStorage.removeItem('filterOpenedColumn');
                            }}
                          />
                        )}
                        filter={'date'}
                        sortable={true}
                        cells={{ 
                          data: (props) => <DateTimeCellWithBackGroundComponent  {...props} />,
                          headerCell:IconHeaderCell
                        }}
                      />
                    :
                    column.field == 'info' ?
                    <Column
                      field={column.field}
                      title={column.title}
                      filterable={column?.filterable}
                      columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                      className='k-text-center'
                      cells={{ 
                        data: (props) => <InfoTagAlarmComponent  {...props} /> ,
                        headerCell: IconHeaderCell
                      }}
                      filter={column?.filter}
                      format={column?.format}
                      //width={300}
                      editable={false}
                    />
                    :
                    <Column
                      key={index}
                      field={column.field}
                      title={column.title}
                      filterable={column?.filterable}
                      columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                      className='k-text-center'
                      cells={{ headerCell: IconHeaderCell }}
                      filter={column?.filter}
                      format={column?.format}
                      width={'2000'}
                      editable={false}
                    />
                  )
                )
                :
                columns?.columns?.map((column: any, index: any) =>
                  column.show && !props.preview && (
                    <Column
                      field={column.field}
                      title={column.title}
                      filterable={column?.filterable}
                      columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                      className='k-text-center'
                      cells={{ data: (props) => <InfoTagAlarmComponent  {...props} /> }}
                      filter={column?.filter}
                      format={column?.format}
                      width={setWidth(column.minWidth)}
                      editable={false}
                    />
                  )
                )
            }            
            {/* <Column field="Discontinued" filter="boolean" width="180px" />  */}
          </Grid> 
        </ExcelExport>
        <GridPDFExport margin="1cm">
          {
            columns?.length > 0 ?
              columns?.map((column: any, index: any) =>
                column.show && (
                  <Column
                    key={index}
                    field={column.field}
                    title={column.title}
                    filterable={column?.filterable}
                    columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                    className='k-text-center'
                    cells={{ data: (props) => <InfoTagAlarmComponent  {...props} /> }}
                    filter={column?.filter}
                    format={column?.format}
                    width={setWidth(column.minWidth)}
                    editable={column?.editable}
                  />
                )
              )
              :
              columns?.columns?.map((column: any, index: any) =>
                column.show && (
                  <Column
                    key={index}
                    field={column.field}
                    title={column.title}
                    filterable={column?.filterable}
                    columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                    className='k-text-center'
                    cells={{ data: (props) => <InfoTagAlarmComponent  {...props} /> }}
                    filter={column?.filter}
                    format={column?.format}
                    width={setWidth(column.minWidth)}
                    editable={column?.editable}
                  />
                )
              )
          }
          <Grid
            data={processedData}
            dataItemKey={DATA_ITEM_KEY}
            //cellRender={cellRender}
            cells={{ data: CellRender}}
            total={totalItems}
            skip={pageIndex.skip}
            take={pageIndex.take}
            pageable={{
              type: 'input',
              buttonCount: 5,
              pageSizes: [5, 10, 50],
              info: true
            }}
            sortable={true}
            filterable={true}
            //reorderable={false}
            sort={dataState.sort}
            filter={dataState.filter}
            onSelectionChange={onSelectedItemsChange}
            onHeaderSelectionChange={onHeaderSelection}
            onPageChange={pageChange}
            onFilterChange={onFilterChange}
            onColumnReorder={onColumnReorder}
            onColumnResize={onColumnResize}
            onDataStateChange={onDataStateChange}
            scrollable={'none'}
          >
            <GridToolbar>
              <div>
                <h3>{localizationService.toLanguageString('alarms.title', 'NOT_FOUND')}</h3>
              </div>
              {!props.preview ?
                <div>
                  <button
                    className='alarm-page disable-alarm-button'
                    onClick={() => {
                      onHeaderSelection(alarms);
                      setDisableAllAlarms(true)
                    }}
                  >
                    {localizationService.toLanguageString('alarms.disable_all_alarms_button', 'NOT_FOUND')}
                  </button>
                </div>
                : null}
                <Popup
                  anchor={anchor.current && anchor.current.element}
                  show={showPopupSaveConfig}
                  popupClass={"popup-content-save-synoptic-config"}
                >
                  {localizationService.toLanguageString('synoptic_devices.saved_config', 'NOT_FOUND')}
                </Popup>
              <div style={{ right: '15px', float: 'right', position: 'absolute', display: 'flex', gap: '8px' }}>
                {!props.preview ?
                  <div>
                    <Button
                      ref={anchorSave}
                      title={localizationService.toLanguageString('save_config', 'NOT_FOUND')}
                      themeColor={'primary'}
                      size="small"
                      svgIcon={saveIcon}
                      onClick={() => {
                        saveStateToLocalStorage();
                        onClickSaveConfig()
                      }}
                    >
                    </Button>
                  </div>
                  : null}
                {!props.preview ?
                  <div>
                    <Button
                      themeColor={'primary'}
                      size="small"
                      svgIcon={uploadIcon}
                      onClick={loadStateFromLocalStorage}
                      title={localizationService.toLanguageString('load_config', 'NOT_FOUND')}
                    >
                    </Button>
                  </div>
                  : null}
                <div>
                  <CSVLink data={json2csv(alarms)} separator={","}>
                    <Button
                      themeColor={"primary"}
                      size="small"
                      title={localizationService.toLanguageString('export_to_csv', 'NOT_FOUND')}
                      svgIcon={fileCsvIcon}
                    />
                  </CSVLink>
                </div>
                <div>
                  <Button
                    onClick={()=>exportRecords('pdf')}
                    themeColor={"primary"}
                    size="small"
                    title={localizationService.toLanguageString('export_to_pdf', 'NOT_FOUND')}
                    svgIcon={filePdfIcon}
                  />
                </div>
                <div>
                  <Button
                    title={localizationService.toLanguageString('export_to_excel', 'NOT_FOUND')}
                    themeColor={"primary"}
                    size="small"
                    svgIcon={fileExcelIcon}
                    onClick={()=>exportRecords('excel')}
                  />
                </div>
                <ConfiguratorToggleColumnsGrid columns={columns} onChange={onChange} />
              </div>
            </GridToolbar>
            {!props.preview ?
              <Column
                cell={(event:any) =>
                  <td className="k-command-cell">                    
                    <Button
                      id='map'
                      icon="globe"
                      //className="k-icon k-font-icon k-i-globe"
                      style={{ color: '#198792', margin: '8px', border: 'none', background: 'none', outline: 'none' }}
                      title={localizationService.toLanguageString('beacons.map', 'NOT_FOUND')}
                      disabled={event?.dataItem?.mapId ? true : false}
                      onClick={() => showMapFromSelectedAlarm(event)}
                    >
                      {/*<a
                        id='map'
                        title={localizationService.toLanguageString('beacons.map', 'NOT_FOUND')}
                      />*/}
                      {/*<td className="k-icon k-font-icon k-i-globe" style={{ paddingBlock: '0.5rem', cursor: 'pointer' }} ></td>*/}
                    </Button>
                  </td>
                }
                width={50}
                editable={false}
                filterable={false}
              />
              : null}
            {!props.preview ?
              <Column
                title={localizationService.toLanguageString('alarms.deactive', 'NOT_FOUND')}
                width={props.preview ? '0px' : 100}
                filterable={false}
                editable={false}
                cells={{data: toggleAlarm}}
              />
              : null}
            {
              columns?.length > 0 ?
                columns?.map((column: any, index: any) =>
                  column.show && (
                    <GridColumn
                      key={index}
                      field={column.field}
                      title={column.title}
                      filterable={column?.filterable}
                      columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                      className='k-text-center'
                      cells={{ data: (props) => <InfoTagAlarmComponent  {...props} /> }}
                      filter={column?.filter}
                      format={column?.format}
                      width={setWidth(column.minWidth)}
                      editable={column?.editable}
                    />
                  )
                )
                :
                columns?.columns?.map((column: any, index: any) =>
                  column.show && (
                    <GridColumn
                      key={index}
                      field={column.field}
                      title={column.title}
                      filterable={column?.filterable}
                      columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                      className='k-text-center'
                      cells={{ data: (props) => <InfoTagAlarmComponent  {...props} /> }}
                      filter={column?.filter}
                      format={column?.format}
                      width={setWidth(column.minWidth)}
                      editable={column?.editable}
                    />
                  )
                )
            }
          </Grid>
        </GridPDFExport>
        {showPopupInfo &&
          <Window
            title={localizationService.toLanguageString('alarms.alarm_details', 'NOT_FOUND')}
            onClose={()=> setShowPopupInfo(!showPopupInfo)}
            resizable={false}
            initialWidth={400}
            initialHeight={480}
            style={{
              border: '2px solid white',
              background: '#198792',
              color: 'white',
              boxShadow: '0px 0px 0px 2px #198792'
            }}
          >
            <div>
              <p><span style={{ fontWeight: 'bold' }}>{localizationService.toLanguageString('alarms.title', 'NOT_FOUND')}</span>: {alarmInfo?.deviceType ? alarmInfo?.deviceType : "N/D"}</p>
              <p><span style={{ fontWeight: 'bold' }}>{localizationService.toLanguageString('alarms.firmware', 'NOT_FOUND')}</span>: {alarmInfo?.firmware != null ? alarmInfo?.firmware : "N/D"}</p>
              <p><span style={{ fontWeight: 'bold' }}>{localizationService.toLanguageString('alarms.temperature', 'NOT_FOUND')}</span>: {alarmInfo?.temperature != null ? alarmInfo?.temperature : "N/D"}</p>
              <p><span style={{ fontWeight: 'bold' }}>{localizationService.toLanguageString('alarms.pressure', 'NOT_FOUND')}</span>: {alarmInfo?.pressure ? alarmInfo?.pressure : "N/D"}</p>
              <p><span style={{ fontWeight: 'bold' }}>{localizationService.toLanguageString('alarms.battery', 'NOT_FOUND')}</span>: {alarmInfo?.battery != null ? alarmInfo?.battery : "N/D"}</p>
              <p><span style={{ fontWeight: 'bold' }}>{localizationService.toLanguageString('alarms.wifiap', 'NOT_FOUND')}</span>: {alarmInfo?.visibleWiFiAp ? alarmInfo?.visibleWiFiAp : "N/D"}</p>
              <p><span style={{ fontWeight: 'bold' }}>{localizationService.toLanguageString('alarms.longitude', 'NOT_FOUND')}</span>: {alarmInfo?.position?.longitude != null ? alarmInfo?.position?.longitude : "N/D"}</p>
              <p><span style={{ fontWeight: 'bold' }}>{localizationService.toLanguageString('alarms.latitude', 'NOT_FOUND')}</span>: {alarmInfo?.position?.latitude != null ? alarmInfo?.position?.latitude : "N/D"}</p>
              <p><span style={{ fontWeight: 'bold' }}>{localizationService.toLanguageString('alarms.altitude', 'NOT_FOUND')}</span>: {alarmInfo?.position?.elevation != null ? alarmInfo?.position?.elevation : "N/D"}</p>
            </div>
          </Window>
        }
        {props.showPopupAlarm && (
          <Dialog
            title={disableAllAlarms ? localizationService.toLanguageString('alarms.disable_all_alarms', 'NOT_FOUND') : localizationService.toLanguageString('alarms.disable_alarm', 'NOT_FOUND')}
            onClose={props.handlePopupAlarm}
          >
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              style={{
                margin: '10px',
                width: '80px'
              }}
              onClick={props.handlePopupAlarm}
            >
              No
            </button>
            <button
              className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
              style={{
                margin: '10px',
                width: '80px'
              }}
              onClick={disableAlarm}
            >
              {localizationService.toLanguageString('alarms.yes', 'NOT_FOUND')}
            </button>
          </Dialog>
        )}
      
    </div>
  );
};